import { createApiClient } from './api';
import { config } from '../config';
import type { AxiosInstance } from 'axios';
import type { MultiLicenseFulfillmentDto } from './licenseComplianceService';

export interface ComplianceCreditDto {
  courseTitle: string;
  creditHours: number;
  creditType?: string;
  completedAt: string;
  source: string; // Platform | External | Manual
  notes?: string;
}

/**
 * Slim per-license subject/manual requirement fulfillment — the same verdicts
 * the member sees, without year buckets or evidence.
 */
export interface AdminSubjectRequirementDto {
  name: string;
  targetKind: string; // 'manual' | 'total_period' | 'per_year'
  /**
   * Provenance category type ('ethics', 'subject_area', 'regulatory', 'custom')
   * from the state category this row was prefilled from; null for user-created rows.
   */
  categoryType?: string | null;
  requiredHours: number;
  earnedHours: number;
  isMet: boolean;
  fulfilledAt?: string | null;
}

export interface AdminLicenseComplianceDto {
  licenseId: string;
  stateCode: string;
  isPrimary: boolean;
  status: 'not_set_up' | 'unconfirmed' | 'modified' | 'confirmed';
  /**
   * 'home_state_reciprocity' = derived row: compliance mirrors the member's
   * primary license, so hour/target fields come back null.
   */
  complianceMode: 'standard' | 'home_state_reciprocity';
  targetHours?: number | null;
  ethicsTargetHours?: number | null;
  earnedHours?: number | null;
  ethicsEarnedHours?: number | null;
  /**
   * Carry-over hours applied to this license (already included in earnedHours).
   * Null when none applied.
   */
  carryoverHours?: number | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  /**
   * Server-side verdict (hours + ethics + every subject/manual requirement met).
   * Null for not-set-up and derived (reciprocity) rows.
   */
  isCompliant?: boolean | null;
  /** Empty for not-set-up and derived rows. */
  subjectRequirements: AdminSubjectRequirementDto[];
}

export interface UserComplianceSummaryDto {
  userId: string;
  fullName: string;
  email: string;
  stateCode?: string;
  totalCreditHours: number;
  creditsByCategory: Record<string, number>;
  credits: ComplianceCreditDto[];
  /** Per-license compliance provenance; earned hours use each license's own confirmed period. */
  licenses: AdminLicenseComplianceDto[];
}

export interface TenantComplianceReportDto {
  tenantName: string;
  /** Null when the report covers all recorded credits (no period filter). */
  periodStart: string | null;
  periodEnd: string | null;
  generatedAt: string;
  users: UserComplianceSummaryDto[];
}

class ComplianceReportService {
  private api: AxiosInstance;

  constructor() {
    this.api = createApiClient(config.api.courses);
  }

  /**
   * Fetches the tenant compliance report. When no period is supplied the
   * report covers all recorded credits (periodStart/periodEnd come back null).
   */
  async getComplianceReport(
    tenantId: string,
    periodStart?: string,
    periodEnd?: string
  ): Promise<TenantComplianceReportDto> {
    const response = await this.api.get<TenantComplianceReportDto>(
      `/api/tenants/${tenantId}/compliance/report`,
      { params: periodStart && periodEnd ? { periodStart, periodEnd } : undefined }
    );
    return response.data;
  }

  /**
   * Full per-license fulfillment for one tenant member — identical shape to what
   * the member sees on their own license management page (subject requirements,
   * per-year buckets, ethics).
   */
  async getUserFulfillment(
    tenantId: string,
    userId: string
  ): Promise<MultiLicenseFulfillmentDto> {
    const response = await this.api.get<MultiLicenseFulfillmentDto>(
      `/api/tenants/${tenantId}/compliance/users/${userId}/fulfillment`
    );
    return response.data;
  }

  async downloadUserPdf(
    user: UserComplianceSummaryDto,
    report: TenantComplianceReportDto
  ): Promise<void> {
    const { default: jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');

    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 48;
    const contentWidth = pageWidth - margin * 2;

    // ── Header bar ──────────────────────────────────────────────────────────
    doc.setFillColor(86, 151, 55);
    doc.rect(0, 0, pageWidth, 72, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('CPE Credit Report', margin, 30);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(report.tenantName, margin, 48);
    const periodLabel =
      report.periodStart && report.periodEnd
        ? `${formatDate(report.periodStart)} – ${formatDate(report.periodEnd)}`
        : 'All recorded credits';
    doc.text(`Reporting Period: ${periodLabel}`, margin, 62);

    // ── Licensee Info ────────────────────────────────────────────────────────
    let y = 96;
    doc.setTextColor(33, 33, 33);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Licensee Information', margin, y);
    y += 4;
    doc.setDrawColor(86, 151, 55);
    doc.setLineWidth(1);
    doc.line(margin, y, margin + contentWidth, y);
    y += 14;

    const infoRows: [string, string][] = [
      ['Name', user.fullName],
      ['Email', user.email],
      ['State', user.stateCode ?? 'PA'],
      ['Report Generated', formatDate(report.generatedAt)],
    ];

    doc.setFontSize(10);
    for (const [label, value] of infoRows) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(90, 90, 90);
      doc.text(label + ':', margin, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(33, 33, 33);
      doc.text(value, margin + 100, y);
      y += 16;
    }

    // ── CPE Summary ──────────────────────────────────────────────────────────
    y += 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(33, 33, 33);
    doc.text('CPE Summary', margin, y);
    y += 4;
    doc.setDrawColor(86, 151, 55);
    doc.line(margin, y, margin + contentWidth, y);
    y += 12;

    // Total hours box
    const boxH = 40;
    const boxW = contentWidth / 2 - 4;
    doc.setFillColor(232, 245, 233);
    doc.roundedRect(margin, y, boxW, boxH, 4, 4, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(33, 33, 33);
    doc.text(user.totalCreditHours.toFixed(1) + ' hrs', margin + boxW / 2, y + 18, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(90, 90, 90);
    doc.text('Total CPE Hours Earned', margin + boxW / 2, y + 32, { align: 'center' });

    // Credits count box
    doc.setFillColor(232, 245, 233);
    doc.roundedRect(margin + boxW + 8, y, boxW, boxH, 4, 4, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(33, 33, 33);
    doc.text(String(user.credits.length), margin + boxW + 8 + boxW / 2, y + 18, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(90, 90, 90);
    doc.text('Total Courses / Activities', margin + boxW + 8 + boxW / 2, y + 32, { align: 'center' });

    y += boxH + 20;

    // ── Credits by Category ──────────────────────────────────────────────────
    const categoryEntries = Object.entries(user.creditsByCategory).sort((a, b) => b[1] - a[1]);
    if (categoryEntries.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(33, 33, 33);
      doc.text('Hours by Field of Study', margin, y);
      y += 4;
      doc.setDrawColor(86, 151, 55);
      doc.line(margin, y, margin + contentWidth, y);
      y += 8;

      autoTable(doc, {
        startY: y,
        head: [['Field of Study / Category', 'Hours']],
        body: categoryEntries.map(([cat, hrs]) => [cat, hrs.toFixed(1)]),
        margin: { left: margin, right: margin },
        styles: { fontSize: 9, cellPadding: 5 },
        headStyles: { fillColor: [86, 151, 55], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [243, 250, 241] },
        columnStyles: { 1: { halign: 'center', cellWidth: 80 } },
        foot: [[
          { content: 'Total', styles: { fontStyle: 'bold', halign: 'right' } },
          { content: user.totalCreditHours.toFixed(1), styles: { fontStyle: 'bold', halign: 'center' } },
        ]],
        footStyles: { fillColor: [232, 245, 233], textColor: [33, 33, 33] },
      });

      y = (doc as any).lastAutoTable.finalY + 20;
    }

    // ── Credit Detail Table ──────────────────────────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(33, 33, 33);
    doc.text('Credit Detail', margin, y);
    y += 4;
    doc.setDrawColor(86, 151, 55);
    doc.line(margin, y, margin + contentWidth, y);
    y += 8;

    const tableRows = user.credits.map((c) => [
      c.courseTitle + (c.source !== 'Platform' ? ' ★' : ''),
      c.creditType ?? '—',
      c.creditHours.toFixed(1),
      formatDateShort(c.completedAt),
      c.source,
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Course / Activity', 'Category', 'Hours', 'Date', 'Source']],
      body: tableRows,
      margin: { left: margin, right: margin },
      styles: { fontSize: 8.5, cellPadding: 5 },
      headStyles: { fillColor: [86, 151, 55], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [243, 250, 241] },
      // Fixed widths for the narrow columns; 'auto' hands all remaining width
      // to Course / Activity so long titles fit without excessive wrapping.
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 90 },
        2: { halign: 'center', cellWidth: 42 },
        3: { halign: 'center', cellWidth: 48 },
        4: { halign: 'center', cellWidth: 55 },
      },
      foot: [[
        { content: 'Total', colSpan: 2, styles: { fontStyle: 'bold', halign: 'right' } },
        { content: user.totalCreditHours.toFixed(1), styles: { fontStyle: 'bold', halign: 'center' } },
        { content: '', colSpan: 2 },
      ]],
      footStyles: { fillColor: [232, 245, 233], textColor: [33, 33, 33] },
    });

    // ── Footer ───────────────────────────────────────────────────────────────
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const footerY = doc.internal.pageSize.getHeight() - 24;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `★ denotes External or Manual credit  |  Generated by CPAx Academy  |  ${new Date().toLocaleDateString()}`,
        margin,
        footerY
      );
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, footerY, { align: 'right' });
    }

    const safeName = user.fullName.replace(/[^a-z0-9]/gi, '_');
    const fileSuffix =
      report.periodStart && report.periodEnd
        ? `${report.periodStart.slice(0, 4)}-${report.periodEnd.slice(0, 4)}`
        : report.generatedAt.slice(0, 4);
    doc.save(`CPE_Report_${safeName}_${fileSuffix}.pdf`);
  }
}

function formatDate(dateStr: string): string {
  // Dates from the API are UTC; format in UTC to avoid off-by-one-day shifts in local timezones
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function formatDateShort(dateStr: string): string {
  // m/d/yy keeps the Credit Detail date column narrow (more room for titles)
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: '2-digit',
    month: 'numeric',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export default new ComplianceReportService();
